/**
 * Skill constants for Idle Depths.
 *
 * Defines which skills exist and their default states.
 * Used for character initialization and migration.
 */

import { createSkillProgress } from '../types/skill.js';

import type { SkillProgress, SkillCategory } from '../types/skill.js';

// ============================================================================
// Skill IDs
// ============================================================================

/**
 * All known skill IDs.
 *
 * Update this array when adding new skills.
 * Used for character initialization and migration.
 */
export const ALL_SKILL_IDS = [
  // Gathering Skills
  'mining',
  'woodcutting',
  'fishing',
  'farming',
  'thieving',

  // Artisan Skills
  'smithing',
  'woodworking',
  'cooking',
  'alchemy',
  'spellcrafting',
  'jewelrycrafting',
  'leatherworking',
  'enchanting',
  'construction',

  // Combat Skills
  'melee',
  'defense',
  'ranged',
  'magic',

  // Support Skills
  'agility',
  'prayer',
  'summoning',
  'pets',
] as const;

/**
 * Skill ID type derived from the ALL_SKILL_IDS array.
 */
export type SkillId = (typeof ALL_SKILL_IDS)[number];

/**
 * Skills that are unlocked by default when a character is created.
 *
 * Gathering and combat skills are unlocked by default.
 * Artisan skills require unlocking through gameplay.
 */
export const SKILLS_UNLOCKED_BY_DEFAULT: readonly SkillId[] = [
  // Gathering
  'mining',
  'woodcutting',
  'fishing',
  'farming',
  'thieving',

  // Combat
  'melee',
  'defense',
  'ranged',
  'magic',

  // Support
  'agility',
  'prayer',
  'summoning',
  'pets',
] as const;

/**
 * Skills grouped by category.
 */
export const SKILLS_BY_CATEGORY: Record<SkillCategory, readonly SkillId[]> = {
  gathering: ['mining', 'woodcutting', 'fishing', 'farming', 'thieving'],
  artisan: [
    'smithing',
    'woodworking',
    'cooking',
    'alchemy',
    'spellcrafting',
    'jewelrycrafting',
    'leatherworking',
    'enchanting',
    'construction',
  ],
  combat: ['melee', 'defense', 'ranged', 'magic'],
  support: ['agility', 'prayer', 'summoning', 'pets'],
};

// ============================================================================
// Skill Initialization
// ============================================================================

/**
 * Check if a skill is unlocked by default.
 *
 * @param skillId - The skill ID to check
 * @returns True if the skill is unlocked by default
 */
export function isSkillUnlockedByDefault(skillId: string): boolean {
  return (SKILLS_UNLOCKED_BY_DEFAULT as readonly string[]).includes(skillId);
}

/**
 * Initialize skills for a character.
 *
 * Handles both new characters and migration of existing characters
 * when new skills are added to the game.
 *
 * @param existingSkills - Existing skills (if migrating)
 * @returns Complete skills record with all known skills
 */
export function initializeSkills(
  existingSkills?: Record<string, SkillProgress>
): Record<string, SkillProgress> {
  const skills: Record<string, SkillProgress> = { ...existingSkills };

  // Add any missing skills (handles migration)
  for (const skillId of ALL_SKILL_IDS) {
    if (!skills[skillId]) {
      skills[skillId] = createSkillProgress(skillId, isSkillUnlockedByDefault(skillId));
    }
  }

  return skills;
}

/**
 * Validate that a skills record has all required skills.
 *
 * @param skills - Skills record to validate
 * @returns True if all required skills are present
 */
export function hasAllRequiredSkills(skills: Record<string, SkillProgress>): boolean {
  // Currently just checks that the object has keys
  // In future, could check for specific required skills
  return Object.keys(skills).length > 0;
}

/**
 * Get the category of a skill.
 *
 * @param skillId - The skill ID
 * @returns The skill category, or undefined if not found
 */
export function getSkillCategory(skillId: string): SkillCategory | undefined {
  for (const [category, skills] of Object.entries(SKILLS_BY_CATEGORY)) {
    if ((skills as readonly string[]).includes(skillId)) {
      return category as SkillCategory;
    }
  }
  return undefined;
}
